import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Accordion } from 'react-bootstrap';

import { FormInput } from '@components/Common/AwpFormInputs';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';

import * as PresetButtons from '../Helpers/GraphPresetButtons';
import * as Actions from '../Helpers/Actions';
import { WeatherSection } from './WeatherSection';

/**
 * DataGraphSection is a React component that displays a list of data graphs
 * within an Accordion component. Each data graph can be expanded or collapsed.
 *
 * @param {React.Ref} ref - A React ref that can be forwarded to child components.
 * @param {Object} data - Data containing a list of data graphs.
 * @param {boolean} readOnly - Flag indicating if the component is in read-only mode.
 * @returns {React.Component} A React component representing the DataGraphSection.
 */
export const DataGraphSection = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [activeGraphKey, setActiveGraphKey] = useState(null);

  /**
   * Handle manual graph toggle to expand or collapse a data graph section.
   *
   * @param {string} eventKey - The unique key of the data graph section.
   */
  const handleManualGraphToggle = (eventKey) => {
    setActiveGraphKey(activeGraphKey === eventKey ? null : eventKey);
  };

  return (
    <>
      {props.data ? (
        <Accordion activeKey={activeGraphKey}>
          {props.data.DataGraphs?.map((dataGraph, idx) => {
            const sectionId = `Graph_${dataGraph.DisplayOrder}`;
            const graphName = dataGraph.Name ? dataGraph.Name : `GRAPH #${idx + 1}`;
            const value = dataGraph.Name ? dataGraph.Name : '';

            return (
              <Card key={sectionId} border="light" className="page-card">
                <CardHeaderToggle
                  eventKey={sectionId}
                  as="h5"
                  title={graphName}
                  onClick={() => handleManualGraphToggle(sectionId)}
                  isLink={false}
                  isLeft={true}
                  extraButtons={props.readOnly ? null : PresetButtons.GraphButtons(sectionId, props.data, props.formik)}
                />
                <Accordion.Collapse eventKey={sectionId}>
                  <Card.Body>
                    <Row>
                      <Col sm={12} md={10} lg={8}>
                        {props.readOnly ? null : (
                          <FormInput
                            label="Graph Name (optional)"
                            instruction="50 Characters max."
                            readOnly={props.readOnly}
                            name={`DataGraphs[${idx}].Name`}
                            id={`DataGraphs[${idx}].Name`}
                            placeholder={graphName}
                            value={value}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                // Update the graph name when the Enter key is pressed
                                dispatch(Actions.updateGraphName(props.data.GraphPresetId, idx, e.target.value));
                              }
                              props.formik.setFieldTouched(`DataGraphs[${idx}].Name`, true);
                            }}
                            onBlur={(e) => {
                              // Update the graph name when the input field loses focus
                              dispatch(Actions.updateGraphName(props.data.GraphPresetId, idx, e.target.value));
                              props.formik.setFieldTouched(`DataGraphs[${idx}].Name`, true);
                            }}
                          />
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {/* Render the WeatherSection component for this dataGraph */}
                        <WeatherSection
                          ref={ref}
                          data={dataGraph}
                          formik={props.formik}
                          dataGraphIndex={idx}
                          readOnly={props.readOnly}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            );
          })}
        </Accordion>
      ) : null}
    </>
  );
});

// Display name for the component
DataGraphSection.displayName = 'DataGraphSection';
